/**
 * Derive security scalars from a RAW row via the 'v3' stream (plan §0.3).
 */
import { WINDOW_KEYS, type IndexCode, type Security, type WindowKey } from '@meridian/contracts';

import {
  BETA_BASE,
  BETA_F0_LO,
  BETA_F0_SPAN,
  DD_EPSILON,
  DD_FLOOR,
  DD_GEN_ADD,
  DD_GEN_LO,
  DD_GEN_SPAN,
  FACTOR_JITTER,
  IDIO_LO,
  IDIO_SPAN,
  PRICE_BASE,
  PRICE_SPAN,
  RETURN_NOISE,
  RETURN_SPREADS,
  VK_LO,
  VK_SPAN,
} from '../constants/generation';
import { SECP } from '../constants/sectors';
import { WYR } from '../constants/windows';
import { fnv1a } from '../rand/fnv1a';
import { createRng } from '../rand/mulberry32';
import { seedSecurityScalars } from '../rand/seed';

import { factorNorm } from './factor-norm';
import { indexMembership } from './index-membership';

import type { RawUniverseRow } from './parse-universe';
import type { Rng } from '../rand/types';

/**
 * Synthetic security: all wire-optional synthetic fields are required and
 * ret/dd are non-null per window (derived path always materializes them).
 *
 * The four fundamentals are narrowed to non-null for the same reason. On the
 * wire they are nullable because a live vendor genuinely may not report a P/E,
 * and a missing P/E is not a P/E of zero (R2b). `RawUniverseRow` types all four
 * as `number`, so the SYNTHETIC path cannot produce a null — inheriting the wire
 * nullability here would make every synthetic consumer carry a branch it can
 * never reach, and `?? 0` is exactly what gets written to silence it. Null stays
 * expressible where it means something: on `Security`.
 */
export type DerivedSecurity = Omit<
  Security,
  'f' | 'nrm' | 'ret' | 'dd' | 'idx' | 'mcap' | 'pe' | 'div' | 'rev' | 'sharesOut' | 'vol' | 'beta'
> & {
  readonly mcap: number;
  readonly pe: number;
  readonly div: number;
  readonly rev: number;
  readonly sharesOut: number;
  /* Narrowed for the same reason as the four fundamentals below the header:
     ADR-0022 made these nullable on the WIRE for live mode, and the synthetic
     path cannot produce a null. Inheriting the nullability would hand every
     synthetic consumer an unreachable branch to silence with `?? 0`. */
  readonly vol: number;
  readonly beta: number;
  readonly f: readonly number[];
  readonly nrm: number;
  readonly ret: Readonly<Record<WindowKey, number>>;
  readonly dd: Readonly<Record<WindowKey, number>>;
  readonly idx: readonly IndexCode[];
};

/**
 * Widen a derived row to the wire shape. Arrays are COPIED rather than passed
 * through: `readonly number[]` is not assignable to the schema's `number[]`, and
 * handing out the frozen instance would make the wire row an alias of the
 * shared universe object.
 */
export function toWireSecurity(s: DerivedSecurity): Security {
  return {
    ...s,
    idx: [...s.idx],
    f: [...s.f],
    ret: { ...s.ret },
    dd: { ...s.dd },
  };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

function round4(n: number): number {
  return Math.round(n * 10000) / 10000;
}

interface WindowDeriveCtx {
  readonly R: Rng;
  readonly vol: number;
  readonly vk: number;
  readonly drifts: readonly [number, number, number, number];
  readonly dev: number;
}

function windowRetDd(ctx: WindowDeriveCtx): {
  readonly ret: Readonly<Record<WindowKey, number>>;
  readonly dd: Readonly<Record<WindowKey, number>>;
} {
  const pairs = WINDOW_KEYS.map((w, i) => {
    /* WINDOW_KEYS, RETURN_SPREADS and SECP.d are length-aligned by plan §0.2.
       The cast is safe by construction, not by comment: RETURN_SPREADS carries a
       `satisfies { length: typeof WINDOW_KEYS['length'] }` guard, so adding a
       fifth window key is a compile error there rather than an `undefined` here
       (which would flow on as NaN and serialise as null). */
    const idx = i as 0 | 1 | 2 | 3;
    const spread = RETURN_SPREADS[idx] * ctx.vk;
    const drift = ctx.drifts[idx];
    const r = round4(drift + ctx.dev * spread + (ctx.R() * 2 - 1) * spread * RETURN_NOISE);
    const gen = -(ctx.vol * Math.sqrt(WYR[w]) * (DD_GEN_LO + ctx.R() * DD_GEN_SPAN) + DD_GEN_ADD);
    const d = Math.max(DD_FLOOR, Math.min(gen, Math.min(0, r) - DD_EPSILON));
    return [w, r, d] as const;
  });
  const ret = Object.fromEntries(pairs.map(([w, r]) => [w, r])) as Record<WindowKey, number>;
  const dd = Object.fromEntries(pairs.map(([w, , d]) => [w, d])) as Record<WindowKey, number>;
  return { ret: Object.freeze(ret), dd: Object.freeze(dd) };
}

function deriveFactors(R: Rng, base: readonly number[]) {
  const f = base.map((x) => x + (R() * 2 - 1) * FACTOR_JITTER);
  const idio = IDIO_LO + R() * IDIO_SPAN;
  return { f, nrm: factorNorm(f, idio) };
}

export function deriveSecurity(row: RawUniverseRow): DerivedSecurity {
  const sectorParams = SECP[row.sc];
  const R = createRng(fnv1a(seedSecurityScalars(row.ticker)));
  const price = round2(PRICE_BASE + R() * PRICE_SPAN);
  const vk = VK_LO + R() * VK_SPAN;
  const vol = round3(sectorParams.v * vk);
  const beta = round2(BETA_BASE + Math.abs(sectorParams.f[0]) * (BETA_F0_LO + R() * BETA_F0_SPAN));
  const dev = R() * 2 - 1;
  const { ret, dd } = windowRetDd({ R, vol, vk, drifts: sectorParams.d, dev });
  const { f, nrm } = deriveFactors(R, sectorParams.f);
  /*
   * No extra v3 draw: shares outstanding is mcap (billions) / price.
   * `price` is round2(PRICE_BASE + R() * PRICE_SPAN) with PRICE_BASE = 26 and
   * R() on [0, 1), so it is always >= 26 — the divide cannot be by zero. A
   * `price > 0 ? … : 0` guard here would claim zero shares outstanding for an
   * unreachable case and then feed that 0 into syntheticAvgVolume (R2b).
   */
  const sharesOut = (row.mcap * 1e9) / price;
  /* SecuritySchema types idx/f as mutable wire arrays; runtime freezes them. */
  const security: DerivedSecurity = Object.freeze({
    ticker: row.ticker,
    name: row.name,
    sc: row.sc,
    sector: row.sector,
    mcap: row.mcap,
    pe: row.pe,
    div: row.div,
    rev: row.rev,
    sharesOut,
    price,
    vol,
    beta,
    ret,
    dd,
    idx: Object.freeze([...indexMembership(row.ticker)]),
    f: Object.freeze([...f]),
    nrm,
  });
  return security;
}
